import React, { useState, useEffect } from "react";
import AthleteLayout from "../common/AthleteLayout";
import { useAthleteAuthContext } from "../login/useAthleteAuthContext";
import { useNavigate } from "react-router-dom";
import { linearRegression, linearRegressionLine } from "simple-statistics";
import { CircularProgressbarWithChildren, buildStyles } from "react-circular-progressbar";
import { AthleteHeader } from "../common/AthleteHeader";
import API_URL from "../../api/API";

export const AthleteLoadvelocityTestResult = () => {
    const { athlete } = useAthleteAuthContext();
    const navigate = useNavigate();
  
    const [maxSpeeds, setMaxSpeeds] = useState([0, 0, 0, 0]);
    const [load, setLoad] = useState([0, 0, 0, 0]);
    const [athleteWeight, setAthleteWeight] = useState(null);
    const [error, setError] = useState(""); // State for error message


    const [results, setResults] = useState(
      { slope: 0, 
        rSquared: 0,
        trainingFocus: '', 
        training_focus_template_id: '', 
        target_speed_technical:0, target_load_technical:0, 
        target_speed_speedstrength:0,  target_load_speedstrength:0,
        target_speed_power:0, target_load_power:0, 
        target_speed_strengthspeed:0, target_load_strengthspeed:0,
      });
    const [isDataSent, setIsDataSent] = useState(false);
    const [training_template_id,setTrainingTemplateId] = useState('');

    const [showPopup, setShowPopup] = useState(false); // State to show the popup


    useEffect(() => {
      const fetchAthleteWeight = async () => {
        try {
          const response = await fetch(`${API_URL.GET_ATHLETE_BY_ID_FRONT}/${athlete.id}`, {
            headers: { Authorization: `Bearer ${athlete.token}` },
          });
  
          if (!response.ok) throw new Error("Failed to fetch athlete weight");
  
          const data = await response.json();
          setAthleteWeight(data.weight);
        } catch (err) {
          setError(err.message);
        }
      };
  
      fetchAthleteWeight();
    }, [athlete.id, athlete.token]);

    function calculateTargetSpeed(ps, step4Speed, trainingFocus) {

      console.log(ps + " - " + step4Speed + " - " + trainingFocus);
      // Check if the Step 4 speed is blank
      if (!step4Speed) {
          return ''; // Return empty string if Step 4 speed is blank
      }
  
      // Determine the target speed based on the training focus
      let ts = 0;
  
      switch (trainingFocus) {
          case 'Technical':
              ts = ps * 0.9;
              break;
          case 'Speed - Strength':
              ts = ps * 0.75;
              break;
          case 'Power':
              ts = ps * 0.5;
              break;
          case 'Strength - Speed':
              ts = ps * 0.4;
              break;
          default:
              ts=0;
              return 'Invalid training focus';
      }
  
      // Round up to the nearest tenth
    return Math.round(ts * 10) / 10;
  }

  // Function to compute Target Load based on Training Focus
const calculateTargetLoad = (trainingFocus, yIntercept, slope) => {
  if (!yIntercept || !slope) {
      return null; // Return null if calculation fails
  }

  // Load multipliers based on training focus
  const focusMultipliers = {
      "Technical": 0.9,
      "Speed - Strength": 0.75,
      "Power": 0.5,
      "Strength - Speed": 0.4
  };

  const multiplier = focusMultipliers[trainingFocus];

  if (multiplier === undefined) {
      return null; // Invalid training focus
  }

  // Calculate Target Load
  const targetLoad = Math.round(((yIntercept * multiplier) - yIntercept) / slope);

  // Ensure max load is 20
  return Math.min(targetLoad, 20);
};

    // Function to calculate the Y-Intercept using linear regression
    const calculateYIntercept = (maxSpeeds, loads) => {
      if (maxSpeeds.length !== loads.length || maxSpeeds.length === 0) {
          return null; // Return null if data is inconsistent
      }
  
      const n = maxSpeeds.length;
      const sumX = loads.reduce((a, b) => a + b, 0);
      const sumY = maxSpeeds.reduce((a, b) => a + b, 0);
      const sumXY = maxSpeeds.reduce((sum, y, i) => sum + y * loads[i], 0);
      const sumX2 = loads.reduce((sum, x) => sum + x * x, 0);
  
      // Calculate slope (m) and intercept (b) for y = mx + b
      const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX ** 2);
      const yIntercept = (sumY - slope * sumX) / n;
  
      return { yIntercept, slope };
  };
  
    const computeR2Manually = (actual, predicted) => {
      const meanActual = actual.reduce((sum, value) => sum + value, 0) / actual.length;
  
      const ssTotal = actual.reduce((sum, value) => sum + Math.pow(value - meanActual, 2), 0);
      const ssResidual = actual.reduce((sum, value, index) => sum + Math.pow(value - predicted[index], 2), 0);
  
      const rSquared = 1 - (ssResidual / ssTotal);
      return rSquared;
    };
  
    const computeLoadVelocityResults = (loads, maxSpeeds) => {
      const numericLoads = loads.map((load) => Number(load));
      const numericMaxSpeeds = maxSpeeds.map((speed) => Number(speed));
  
      const data = numericLoads.map((load, index) => [load, numericMaxSpeeds[index]]);
      const regression = linearRegression(data);
      const regressionLine = linearRegressionLine(regression);
  
      const predictedSpeeds = numericLoads.map((load) => regressionLine(load));
  
      console.log("numericMaxSpeeds:", numericMaxSpeeds);
      console.log("Predicted Speeds:", predictedSpeeds);
  
      try {
        // Manual R-squared calculation
        const rsq = computeR2Manually(numericMaxSpeeds, predictedSpeeds);
        console.log("Manual R-squared:", rsq);

        //compute the peak speed from maxSpeeds array
        const peakSpeed = Math.max(maxSpeeds[0], maxSpeeds[1], maxSpeeds[2], maxSpeeds[3]);
        console.log("Peak Speed: " + peakSpeed);

        const { yIntercept, slope } = calculateYIntercept(maxSpeeds, loads);
        console.log("YIntercept: " + yIntercept);
        console.log("Slope: " + slope);
  
        return {
          slope: regression.m.toFixed(3),
          rSquared: Math.round((rsq * 100)), // R-squared in percentage
          
          target_speed_technical : calculateTargetSpeed(peakSpeed,maxSpeeds[3],"Technical"),
          target_load_technical: calculateTargetLoad("Technical",yIntercept,slope),
         
          target_speed_speedstrength: calculateTargetSpeed(peakSpeed,maxSpeeds[3],"Speed - Strength"),
          target_load_speedstrength: calculateTargetLoad("Speed - Strength",yIntercept,slope),

          target_speed_strengthspeed: calculateTargetSpeed(peakSpeed,maxSpeeds[3],"Strength - Speed"),
          target_load_strengthspeed:  calculateTargetLoad("Strength - Speed",yIntercept,slope),

          target_speed_power: calculateTargetSpeed(peakSpeed,maxSpeeds[3],"Power"),
          target_load_power: calculateTargetLoad("Power",yIntercept,slope),

        };
      } catch (error) {
        console.error("Error calculating R-squared:", error);
        return {
          slope: regression.m.toFixed(3),
          rSquared: "Error",
        };
      }
    };

    const getTrainingFocusFromSlope = (slope) => {
        if (slope < -0.800) {
          return "Technical";
        } else if (slope < -0.700) {
          return "Speed - Strength";
        } else if (slope < -0.500) {
          return "Power";
        } else {
          return "Strength - Speed";
        }
      };

      //later make this dynamic API call
      const getTemplateIdByTrainingFocus = (trainingFocus) => {
        const focusMap = {
          "Technical": "67a1e2abf4be91c3db22ba03",
          "Speed - Strength": "67a1e2d3f4be91c3db22ba04",
          "Power": "67a1e318f4be91c3db22ba05",
          "Strength - Speed": "67a1e34ff4be91c3db22ba06"
        };
      
        return focusMap[trainingFocus] || null;  // Return null if the focus is not found
      };

      //check for existing test results if any.
      const fetchTestResults = async () => {
      /*  try {
            const response = await fetch(API_URL.GET_ATHLETE_LOADVELOCITY_TEST_RESULT + athlete.id, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${athlete.token}`,
                },
            });
            
            if (!response.ok) {
               // throw new Error("Failed to fetch test results");
            }
            
            const data = await response.json();
            console.log(data);
              // If test results are found, set session data to values from the database
            if (data.data && data.data.rsquared) {
              console.log("Setting session data from database");
              sessionStorage.setItem("lvmaxSpeed1", data.data.maxspeed1 || 0);
              sessionStorage.setItem("lvmaxSpeed2", data.data.maxspeed2 || 0);
              sessionStorage.setItem("lvmaxSpeed3", data.data.maxspeed3 || 0);
              sessionStorage.setItem("lvmaxSpeed4", data.data.maxspeed4 || 0);

              // Update the state with values from the database
               setMaxSpeeds([data.data.maxspeed1, data.data.maxspeed2, data.data.maxspeed3, data.data.maxspeed4].filter(speed => !isNaN(speed)));
                 // Update the state with values from the database
               setLoad([data.data.load1, data.data.load2, data.data.load3, data.data.load4].filter(load => !isNaN(load)));
            }
            else{
                const maxSpeed1 = parseFloat(sessionStorage.getItem("lvmaxSpeed1")) || 0;
                const maxSpeed2 = parseFloat(sessionStorage.getItem("lvmaxSpeed2")) || 0;
                const maxSpeed3 = parseFloat(sessionStorage.getItem("lvmaxSpeed3")) || 0;
                const maxSpeed4 = parseFloat(sessionStorage.getItem("lvmaxSpeed4")) || 0;
                const speeds = [maxSpeed1, maxSpeed2, maxSpeed3, maxSpeed4].filter((speed) => !isNaN(speed));
                console.log("Max SPeeds Session");
                setMaxSpeeds(speeds);
                console.log(speeds);

                const _load1 = 0;
                const _load2 = (25*athleteWeight)/100;
                const _load3 = (50*athleteWeight)/100;
                const _load4 = (75*athleteWeight)/100;
                setLoad([_load1, _load2,_load3, _load4].filter(load => !isNaN(load)));
            }


        } catch (error) {
            console.log(error.message);
        }
        */
        const _load1 = 0;
        const _load2 = (25*athleteWeight)/100;
        const _load3 = (50*athleteWeight)/100;
        const _load4 = (75*athleteWeight)/100;
        setLoad([_load1, _load2,_load3, _load4].filter(load => !isNaN(load)));
        
        const maxSpeed1 = parseFloat(sessionStorage.getItem("lvmaxSpeed1")) || 0;
        const maxSpeed2 = parseFloat(sessionStorage.getItem("lvmaxSpeed2")) || 0;
        const maxSpeed3 = parseFloat(sessionStorage.getItem("lvmaxSpeed3")) || 0;
        const maxSpeed4 = parseFloat(sessionStorage.getItem("lvmaxSpeed4")) || 0;

        console.log(maxSpeed1 + "-" + maxSpeed2 + "-" + maxSpeed3 + "-" + maxSpeed4);
        //ifall of the speeds are 0 then in that case there is no session data retained. but the user can have completed the test so check the database for the records
        //if it exists. 
        if(maxSpeed1===0 && maxSpeed2===0 && maxSpeed3===0 && maxSpeed4===0)
        {
              try {
                      const response = await fetch(API_URL.GET_ATHLETE_LOADVELOCITY_TEST_RESULT + athlete.id, {
                          method: "GET",
                          headers: {
                              "Authorization": `Bearer ${athlete.token}`,
                          },
                      });
                      
                      if (!response.ok) {
                        // throw new Error("Failed to fetch test results");
                      }
                      
                      const data = await response.json();
                      console.log(data);
                        // If test results are found, set session data to values from the database
                      if (data.data && data.data.rsquared) {
                        console.log("Setting session data from database");
                        sessionStorage.setItem("lvmaxSpeed1", data.data.maxspeed1 || 0);
                        sessionStorage.setItem("lvmaxSpeed2", data.data.maxspeed2 || 0);
                        sessionStorage.setItem("lvmaxSpeed3", data.data.maxspeed3 || 0);
                        sessionStorage.setItem("lvmaxSpeed4", data.data.maxspeed4 || 0);

                        // Update the state with values from the database
                        setMaxSpeeds([data.data.maxspeed1, data.data.maxspeed2, data.data.maxspeed3, data.data.maxspeed4].filter(speed => !isNaN(speed)));
                          // Update the state with values from the database
                       // setLoad([data.data.load1, data.data.load2, data.data.load3, data.data.load4].filter(load => !isNaN(load)));
                        }
                }
                catch(error){
                  console.log(error.message);
                }
        }
        else{
              const speeds = [maxSpeed1, maxSpeed2, maxSpeed3, maxSpeed4].filter((speed) => !isNaN(speed));
              console.log("Max SPeeds Session");
              setMaxSpeeds(speeds);
              console.log(speeds);
        }

    
    };
  
    useEffect(() => {
       // First, try fetching the test results from the database
      fetchTestResults();

    }, [athleteWeight]);
  
    useEffect(() => {
        if (maxSpeeds.length > 0 && load.length > 0) {
            const computedResults = computeLoadVelocityResults(load, maxSpeeds);
            const focus = getTrainingFocusFromSlope(computedResults.slope); // Get initial training focus based on slope
            const newResults = { ...computedResults, trainingFocus: focus };
            setResults(newResults);
    
            // Check R-squared and decide whether to save or delete
            const rSquaredValue = parseFloat(newResults.rSquared);
    
            if (rSquaredValue >= 98) {
                // Save results only if R-squared is 95% or greater
                saveResultsToServer(newResults);
            } else if (rSquaredValue < 98) {
                // Delete the record only if R-squared is less than 95
                deleteTestResultsFromServer();
            }
        }
    }, [maxSpeeds, load]);

    const saveResultsToServer = async (results) => {
        // Ensure athlete is authenticated
        if (!athlete || !athlete.token) {
          console.error("Athlete is not authenticated.");
          return;
        }
      
        setTrainingTemplateId(getTemplateIdByTrainingFocus(results.trainingFocus));

        // Prepare the data object to send to the server
        const dataToSend = {
          maxspeed1: maxSpeeds[0],
          maxspeed2: maxSpeeds[1],
          maxspeed3: maxSpeeds[2],
          maxspeed4: maxSpeeds[3],
          load1: load[0],
          load2: load[1],
          load3: load[2],
          load4: load[3],
          rsquared: results.rSquared,
          slope: results.slope,
          training_focus: results.trainingFocus,
          training_focus_template_id: getTemplateIdByTrainingFocus(results.trainingFocus), // Replace with actual logic if required
          target_speed_technical: results.target_speed_technical,
          target_load_technical: results.target_load_technical,
          target_speed_speedstrength: results.target_speed_speedstrength,
          target_load_speedstrength: results.target_load_speedstrength,
          target_speed_strengthspeed: results.target_speed_strengthspeed,
          target_load_strengthspeed: results.target_load_strengthspeed,
          target_speed_power: results.target_speed_power,
          target_load_power: results.target_load_power,
       
        };
      
        try {
          const response = await fetch(API_URL.POST_ATHLETE_LOADVELOCITY_TEST_RESULT + athlete.id, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${athlete.token}`,
            },
            body: JSON.stringify(dataToSend),
          });
      
          if (response.ok) {
            const result = await response.json();
            console.log("Test results saved successfully:", result);
            setIsDataSent(true); // Update state to indicate success
          } else {
            const errorData = await response.json();
            console.error("Failed to save test results:", errorData.message || response.statusText);
          }
        } catch (error) {
          console.error("Error saving test results:", error);
        }
      };

      const deleteTestResultsFromServer = async () => {
        if (!athlete || !athlete.token) {
          console.error("Athlete is not authenticated.");
          return;
        }
      
        try {
          // Send DELETE request to remove the test results from the server
          const response = await fetch(`${API_URL.DELETE_ATHLETE_LOADVELOCITY_TEST_RESULT}${athlete.id}`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${athlete.token}`,
              },
          });
      
          if (response.ok) {
            console.log("Test results deleted successfully.");
          } else {
            const errorData = await response.json();
            console.error("Failed to delete test results:", errorData.message || response.statusText);
          }
        } catch (error) {
          console.error("Error deleting test results:", error);
        }
      };


    const isLowRsq = parseFloat(results.rSquared) < 98;
  
    const handleRetakeTest = () => {
        navigate('/athlete/tools/loadvelocity/test/step1');
    };
  
   const handleDataDeleteAndRetakeTest = () => {
      deleteTestResultsFromServer();
      navigate('/athlete/tools/loadvelocity/test/step1');
    }
  
    return (
      <AthleteLayout>
        <div className="bg-[#f6f6f6] h-full">
          <div className="bg-white">
            <AthleteHeader />
          </div>
          <div className="relative h-full bg-[#f6f6f6] pb-5 overflow-y-auto">
            <div className="bg-[#F6F6F6]">
             {isLowRsq ? (
                     <div className="w-full max-w-2xl mx-auto flex flex-row justify-between gap-5 px-10 py-[19px]">
                        <span className="text-base font-normal pb-[4px] text-black border-b border-black">Test</span>
                        <span className="text-base font-normal pb-[4px] text-[#a5a5a5]">Train</span>
                        <span className="text-base font-normal pb-[4px] text-[#a5a5a5]">Performance</span>
                    </div>
             ): (
                    <div className="w-full max-w-2xl mx-auto flex flex-row justify-between gap-5 px-10 py-[19px]">
                      <span className="text-base font-normal pb-[4px] text-black border-b border-black">Test</span>
                      <span onClick={()=> navigate("/athlete/tools/loadvelocity/training-weeks/" + training_template_id)} className="text-base font-normal pb-[4px] text-[#a5a5a5] cursor-pointer ">Train</span>
                      <span className="text-base font-normal pb-[4px] text-[#a5a5a5]">Performance</span>
                    </div>
             )}
           
            </div>
  
            <div className="w-full max-w-2xl mx-auto px-5 pb-5">
              <div className="relative text-center mb-[58px]">
                <p className="text-black text-base font-normal">Test Results</p>
                <div
                  onClick={() => navigate(-1)}
                  className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center gap-2 cursor-pointer"
                >
                  <ArrowL />
                  <span className="text-black text-sm font-normal">Back</span>
                </div>
              </div>
  
              <div className="mx-auto w-fit block mb-[69px]">
                <div style={{ width: 174, height: 174 }}>
                  <CircularProgressbarWithChildren
                    strokeWidth={5}
                    value={results.rSquared}
                    styles={buildStyles({
                      textColor: "#000",
                      pathColor: isLowRsq ? "#FF0000" : "#00901E", // Red if R-squared is low
                      trailColor: "#E5E5E5",
                    })}
                  >
                    <span className="text-black text-4xl">{`${results.rSquared}%`}</span>
                  </CircularProgressbarWithChildren>
                </div>
              </div>
  
              <div className="text-center">
                <p className="text-[#a5a5a5] text-xs font-normal uppercase mb-2">training focus</p>
                <span className="text-black text-sm font-normal">{results.trainingFocus}</span>
  
  
                <div>
                  <h2>Load Velocity Test Results</h2>
                  <p>Slope: {results.slope}</p>
                  <p>R-Squared: {results.rSquared}%</p>
                </div>
  
                {/* Separate Buttons */}
                <div className="flex justify-center gap-4">
                  {isLowRsq ? (
                    <button
                      onClick={handleRetakeTest}
                      className="uppercase text-base font-semibold text-black p-6 w-full max-w-2xl mx-auto border border-black rounded-[70px] hover:opacity-70 mt-[72px]"
                    >
                      Retake Test
                    </button>
                  ) : (
                    <div className="w-full max-w-2xl mx-auto p-6">
                        {/* Start Training Button */}
                        <button
                          onClick={()=> navigate("/athlete/tools/loadvelocity/training-weeks/" + training_template_id)}
                          className="uppercase text-base font-semibold text-black p-6 w-full border border-black rounded-[70px] hover:opacity-70 mt-[72px]"
                        >
                          Start Training
                        </button>
                  
                          {/* Redo Training Button */}
                          <button
                            onClick={() => setShowPopup(true)} // Show the popup when clicking Redo Training
                            className="uppercase text-base font-semibold text-black p-6 w-full border border-black rounded-[70px] hover:opacity-70 mt-[24px]"
                          >
                            Redo Training
                          </button>
              
                            {/* Confirmation Popup */}
                            {showPopup && (
                              <div
                                className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50"
                                onClick={() => setShowPopup(false)} // Close the popup if clicked outside
                              >
                                <div
                                  className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full"
                                  onClick={(e) => e.stopPropagation()} // Prevent the popup from closing if clicked inside
                                >
                                  <h3 className="text-lg font-semibold mb-4">
                                    Are you sure you want to re-compute your training focus? All your previous data will be reset.
                                  </h3>
                                  <div className="flex justify-between">
                                    <button
                                      onClick={() => setShowPopup(false)} // Close the popup
                                      className="px-6 py-3 bg-gray-300 text-black rounded-lg"
                                    >
                                      No
                                    </button>
                                    <button
                                      onClick={handleDataDeleteAndRetakeTest} // Call function to delete test results
                                      className="px-6 py-3 bg-red-500 text-white rounded-lg"
                                    >
                                      Yes
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                  </div>
                  
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </AthleteLayout>
    );
  };
  
  const ArrowL = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
      <path d="M7 13L1 7L7 1" stroke="#000" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  